import React from 'react';
import { useTranslation } from 'react-i18next';

import type { RouteSegment } from '../../../../../types/surveillanceRoute';
import type { AutoReview } from '../../../../../utils/segmentAutoReview';
import type { ReviewDisplay } from '../../../../../utils/segmentReviewOverlay';
import {
  ReviewTd,
  ReviewTarget,
  ReviewCheckBox,
  ChangedMark,
  VisuallyHidden,
} from './styles';

interface ReviewCellProps {
  segment: RouteSegment;
  /** ¿Admite marca? (cerrado y sincronizado). Si no, la celda va con «-». */
  reviewable: boolean;
  display: ReviewDisplay;
  /** Qué lo revisa solo (ROID y estado); solo con `display === 'auto'`. */
  autoReview?: AutoReview;
  onToggle: (segment: RouteSegment, reviewed: boolean) => void;
}

/** Id estable de la celda «Nº» de un tramo: la casilla lo usa como descripción. */
export const segmentNumberCellId = (segmentId: string): string =>
  `segment-number-${segmentId}`;

/**
 * Celda «Revisado» de un tramo. Se pinta SIEMPRE (con «-» si no hay nada que
 * ofrecer) para no descuadrar la fila. Corta el evento hacia la fila: tocar la
 * casilla no selecciona el tramo en el mapa.
 */
export default function ReviewCell({
  segment,
  reviewable,
  display,
  autoReview,
  onToggle,
}: ReviewCellProps): JSX.Element {
  const { t } = useTranslation(['pages', 'common']);

  if (!reviewable) {
    return <ReviewTd onClick={(e) => e.stopPropagation()}>-</ReviewTd>;
  }

  // Revisado solo: casilla marcada y bloqueada, sin acción; el aviso dice por qué.
  if (display === 'auto') {
    const autoTitle = t('pages:surveillance.segmentReview.auto', {
      roid: autoReview?.roid ?? '',
      state: autoReview?.state ? t(`common:states.${autoReview.state}`) : '',
    });
    return (
      <ReviewTd
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        data-testid="segment-review-cell"
      >
        <ReviewTarget>
          <ReviewCheckBox
            indeterminate={false}
            checked
            disabled
            onChange={() => undefined}
            aria-label={autoTitle}
            title={autoTitle}
            data-review-auto="true"
            aria-describedby={segmentNumberCellId(segment.id)}
          />
        </ReviewTarget>
      </ReviewTd>
    );
  }

  // Un toque en la celda: revisado → desmarca; el resto (nada o ámbar) → marca
  // (en ámbar, reconfirma).
  const handleActivate = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onToggle(segment, display !== 'reviewed');
  };

  return (
    <ReviewTd
      onClick={handleActivate}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      data-testid="segment-review-cell"
    >
      <ReviewTarget>
        <ReviewCheckBox
          indeterminate={false}
          checked={display === 'reviewed'}
          // La celda entera gestiona el toque; la casilla solo refleja el estado.
          onChange={() => undefined}
          aria-label={t('pages:surveillance.segmentReview.toggle')}
          // Al pasar el ratón (escritorio) dice para qué es la casilla.
          title={t('pages:surveillance.segmentReview.toggle')}
          // Sin claves i18n nuevas: el lector dice de qué tramo habla leyendo su «Nº».
          aria-describedby={segmentNumberCellId(segment.id)}
        />
        {display === 'changed' && (
          // Solo el icono para que la columna siga estrecha; el texto, en el
          // aviso al pasar el ratón y para el lector de pantalla.
          <ChangedMark
            data-testid="segment-review-changed"
            title={t('pages:surveillance.segmentReview.changed')}
          >
            <span aria-hidden="true">⚠</span>
            <VisuallyHidden>{t('pages:surveillance.segmentReview.changed')}</VisuallyHidden>
          </ChangedMark>
        )}
      </ReviewTarget>
    </ReviewTd>
  );
}
